import { createColumnHelper } from "@tanstack/react-table";
import { format } from "date-fns";
import { comissionPercentage } from "../../../../../../app/utils/comissionPercentage";
import { formatMoneyValue } from "../../../../../../app/utils/formatMoneyValue";
import { Transaction } from "../../../../../../app/entities/Transaction";
import { BasicAvatar } from "../../../../../components/BasicAvatar";
import Status from "../../../../../components/Status";
import { onlyFirstName } from "@/app/utils/onlyFirstName";
import { PartnerType } from "@/app/utils/partners.enum";
import { useAuth } from "@/app/hooks/useAuth";
import { ProductCatalogItem } from "@/app/services/configService/getProductsCatalog";
import { PartnerImage } from "@/view/components/PartnerImage";

const columnHelper = createColumnHelper<Transaction>();

const buildColumns = (products?: ProductCatalogItem[]) => [
  columnHelper.accessor("contract", {
    id: "contract",
    header: () => (
      <div className="flex flex-col items-start pl-2 xl:items-center xl:pl-0">
        <div>Contrato</div>
        <div className="block text-black04 xl:hidden">Vendedor</div>
      </div>
    ),
    cell: ({ row }) => (
      <div className="flex flex-col items-start pl-4 xl:items-center xl:pl-0">
        <div>
          {String(row.original.contract).length !== 24
            ? row.original.contract
            : "-"}
        </div>
        <div className="block text-black04 xl:hidden">
          <p className="text-black04">
            {row.original?.seller?.name
              ? onlyFirstName(row.original.seller.name)
              : "N/A"}
          </p>
          <p className="text-black04">{row.original.team?.name || ""}</p>
        </div>
      </div>
    ),
  }),
  columnHelper.accessor("type", {
    id: "type",
    header: () => <div className="hidden pl-2 text-left xs:block">Produto</div>,
    cell: ({ row }) => (
      <div className="hidden items-center justify-start pl-6 xs:flex">
        <PartnerImage
          type={row.original.type as PartnerType}
          products={products}
        />
      </div>
    ),
  }),
  columnHelper.accessor("customer", {
    id: "customer",
    header: () => <div className="hidden text-left xs:block">Cliente</div>,
    cell: ({ row }) => (
      <div className="hidden text-left xs:block">
        {row.original?.customer?.name
          ? onlyFirstName(row.original.customer.name)
          : ""}
      </div>
    ),
  }),
  columnHelper.accessor("value", {
    id: "value",
    header: () => (
      <div className="flex flex-col items-end pr-2 xs:items-center xs:pr-0">
        <div>Valor</div>
        <div className="block text-black04 lg:hidden">Data</div>
      </div>
    ),
    cell: ({ row }) => (
      <div className="flex flex-col items-end pr-4 xs:items-center xs:pr-0">
        <div>{formatMoneyValue(row.original.value)}</div>
        <div className="block text-black04 lg:hidden">
          <p>
            {format(
              row.original.statusAt ?? row.original.createdAt,
              "dd/MM/yyyy, HH:mm",
            )}
          </p>
          <p className="truncate" title={row.original.activity || ""}>
            {row.original.activity || "-"}
          </p>
        </div>
      </div>
    ),
  }),
  columnHelper.accessor("commission", {
    id: "comission",
    header: () => <div className="hidden text-center sm:block">Comissão</div>,
    cell: ({ row }) => {
      const isSalvcar = row.original.table?.name
        ?.toLowerCase()
        .includes("salvcar");
      return (
        <div className="hidden text-center sm:block">
          {formatMoneyValue(row.original.commission)}{" "}
          {!isSalvcar &&
            comissionPercentage(row.original.value, row.original.commission)}
        </div>
      );
    },
  }),
  columnHelper.accessor("seller", {
    id: "vendedor",
    header: () => (
      <div className="hidden lg:block">
        <div className="text-left">Vendedor</div>
      </div>
    ),
    cell: ({ row }) => (
      <div className="hidden lg:block">
        <div className="flex items-center gap-2">
          <BasicAvatar
            name={row.original?.seller?.name || ""}
            profileImageUrl={row.original?.seller?.profileImageUrl || ""}
          />
          <div className="flex flex-col gap-1">
            <p>
              {row.original?.seller?.name
                ? onlyFirstName(row.original.seller.name)
                : "N/A"}
            </p>
            <p className="text-black04">{row.original.team?.name || ""}</p>
          </div>
        </div>
      </div>
    ),
  }),
  columnHelper.accessor((row) => row.statusAt ?? row.createdAt, {
    id: "data",
    header: () => (
      <div className="hidden text-center xl:block">
        <div>Data</div>
        <div className="text-black04">Atividade</div>
      </div>
    ),
    cell: ({ row }) => (
      <div className="hidden text-center xl:block">
        <div>
          {format(
            row.original.statusAt ?? row.original.createdAt,
            "dd/MM/yyyy, HH:mm",
          )}
        </div>
        <p className="truncate text-black04" title={row.original.activity || ""}>
          {row.original.activity || "-"}
        </p>
      </div>
    ),
  }),
  columnHelper.accessor("actualStatus", {
    id: "actualStatus",
    header: () => <div className="hidden text-center md:block">Status</div>,
    cell: ({ row }) => (
      <div className="hidden md:block">
        <Status status={row.getValue("actualStatus")} />
      </div>
    ),
  }),
];

export const TableColumsDefinition = (products?: ProductCatalogItem[]) => {
  const { isSeller } = useAuth();

  const columns = buildColumns(products);

  return {
    columns: isSeller
      ? columns.filter((column) => column.id !== "vendedor")
      : columns,
  };
};
